import { useState } from 'react';

/**
 * Which right-edge drawer is open (ADR 0044, amending ADR 0029).
 *
 * Every drawer starts closed, home included. Only crossing the home / not-home
 * boundary resets it, so opening an app closes a links panel opened on home, while
 * moving between pages inside an app never closes the assistant.
 */

export const LINKS_DRAWER_ID = 'links';

/** "/" with or without trailing slashes. */
export function isHomePath(pathname: string): boolean {
  return (pathname.replace(/\/+$/, '') || '/') === '/';
}

/** The part of Cloudscape's onDrawerChange event this hook reads. */
export interface DrawerChangeEvent {
  detail: { activeDrawerId: string | null };
}

export interface ActiveDrawer {
  activeDrawerId: string | null;
  onDrawerChange: (event: DrawerChangeEvent) => void;
}

interface DrawerState {
  home: boolean;
  id: string | null;
}

/**
 * Controlled `activeDrawerId` / `onDrawerChange` for AppLayout. `availableIds` is the
 * ids of the drawers offered on this page. An open drawer that stops being offered
 * (the assistant, on leaving an app) is closed, not left dangling.
 */
export function useActiveDrawer(pathname: string, availableIds: readonly string[]): ActiveDrawer {
  const home = isHomePath(pathname);
  const [state, setState] = useState<DrawerState>({ home, id: null });

  // Adjusting state during render, keyed on the previous render's value, is React's
  // documented pattern for "reset when a prop changes". An effect would paint one
  // frame with the old drawer first.
  let current = state;
  if (state.home !== home || (state.id !== null && !availableIds.includes(state.id))) {
    current = { home, id: null };
    setState(current);
  }

  return {
    activeDrawerId: current.id,
    onDrawerChange: (event) => setState({ home, id: event.detail.activeDrawerId }),
  };
}
